import { Text } from '@thanx/uikit/text'
import { useGetInsightsLabQuery } from 'api/insightsLab'
import { useStyletron } from 'baseui'
import Loading from 'components/Loading'
import NoSearchResultsFound from 'components/NoSearchResultsFound'
import SearchInput from 'components/SearchInput'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import { useMemo, useState } from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import CategoryFilter from './CategoryFilter'
import InsightsLabBanner from './InsightsLabBanner'
import ReportItem from './ReportItem'

const t = buildTranslate('insights_lab')

const InsightsLab = () => {
  const [css] = useStyletron()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  )
  const merchant = useCurrentMerchant()

  const {
    data: insightsLab = [],
    isLoading,
    error,
  } = useGetInsightsLabQuery(undefined, {
    skip: !merchant, // Skip the query if there's no merchant
  })

  // Get all unique categories with counts
  const categories = useMemo(() => {
    const categories: { id: string | null; name: string }[] = [
      { id: null, name: t('categories.all') },
    ]
    return categories.concat(
      Array.from(
        new Map(
          insightsLab.map(item => {
            return [
              item.category.toLowerCase(),
              { id: item.category.toLowerCase(), name: item.category },
            ]
          })
        ).values()
      ).sort((a, b) => a.name.localeCompare(b.name))
    )
  }, [insightsLab])

  const filteredInsights = useMemo(() => {
    let results = [...insightsLab]

    // Apply category filter
    if (selectedCategoryId && searchQuery.trim() === '') {
      results = results.filter(
        item => item.category.toLowerCase() === selectedCategoryId
      )
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      results = results.filter(
        item =>
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      )
    }

    return results
  }, [insightsLab, selectedCategoryId, searchQuery])

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
  }

  const handleCategorySelect = (categoryId: string | null) => {
    setSelectedCategoryId(categoryId)
  }

  if (!merchant) return null

  if (isLoading || error || !insightsLab || insightsLab.length === 0) {
    return <Loading />
  }

  return (
    <div className="full-height grey-05-bkg overflow-auto">
      <Container
        fluid
        className="w-100 border-bottom-1 grey-20-border pt-l px-0 white-bkg"
      >
        <Container className="py-m d-flex flex-column mr-auto">
          <Text.Header2 className="m-0 mr-xl">{t('view.title')}</Text.Header2>
          <Text.BodyText3 tag="p" className="mt-xs mb-m">
            {t('view.subtitle')}
          </Text.BodyText3>
        </Container>
      </Container>

      <Helmet>
        <title>{t('view.title')}</title>
      </Helmet>

      <div className="full-height grey-05-bkg overflow-auto pt-l">
        <Container>
          <div className="pb-l">
            <InsightsLabBanner />
          </div>

          <SearchInput value={searchQuery} onChange={handleSearchChange} />

          {filteredInsights.length === 0 ? (
            <div className="pt-l">
              <NoSearchResultsFound />
            </div>
          ) : (
            <div
              className={`pt-l ${css({
                display: 'grid',
                gap: '30px',
                gridTemplateColumns:
                  searchQuery.trim() === '' ? '200px auto' : 'auto',
              })}`}
            >
              {searchQuery.trim() === '' && (
                <div>
                  <CategoryFilter
                    categories={categories}
                    selectedCategoryId={selectedCategoryId}
                    onSelectCategory={handleCategorySelect}
                  />
                </div>
              )}
              <div>
                {searchQuery.trim() === '' && (
                  <Text.Header3 className="mt-0 mb-s">
                    {categories.find(
                      category => category.id === selectedCategoryId
                    )?.name || t('categories.all')?.name}
                  </Text.Header3>
                )}
                {filteredInsights.map(item => (
                  <ReportItem key={item.id} {...item} />
                ))}
              </div>
            </div>
          )}
        </Container>
      </div>
    </div>
  )
}

export default InsightsLab
